import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { applyFormat, renderFormattedText } from '../utils/simpleRichText';
import Icon from './Icon';

// Notes are tied to the meeting record, not the live LiveKit room, so this
// works the same whether or not anyone's actually joined yet.

export const NotesIcon = () => <Icon name="clipboard" size="18px" />;

export const ChevronDownIcon = () => <Icon name="chevron-down" size="12px" />;

export const BookmarkIcon = () => <Icon name="bookmark" size="18px" />;

export const SendIcon = () => <Icon name="paper-plane-empty" size="16px" />;

export const formatElapsed = (totalSeconds) => {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds || 0));
  const h = Math.floor(safeSeconds / 3600);
  const m = Math.floor((safeSeconds % 3600) / 60);
  const s = safeSeconds % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
};

export const formatWallClock = (input) => {
  const d = new Date(input);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12 || 12;
  return `${mm}.${dd}.${yyyy}, ${hours}:${minutes}${ampm}`;
};

// Notes panel (agency only) - timestamped entries, flagged moments,
// compose box with lightweight formatting. Saved with the meeting.
// flagSignal/flaggedAtSeconds only apply once recording's underway, so
// the lobby just never passes them.
export const NotesPanel = ({ meetingId, onToggle, flagSignal, flaggedAtSeconds, collapseIcon: CollapseIcon = ChevronDownIcon }) => {
  const [notes, setNotes] = useState([]);
  const [draft, setDraft] = useState('');
  const [flagMode, setFlagMode] = useState(false);
  const textareaRef = useRef(null);
  const listRef = useRef(null);
  const flagHandledRef = useRef(0);

  useEffect(() => {
    axios
      .get(`/api/livekit/meetings/${meetingId}/notes`, { withCredentials: true })
      .then((res) => {
        if (res.data.success) setNotes(res.data.notes);
      })
      .catch((err) => console.error('Failed to load notes:', err));
  }, [meetingId]);

  useEffect(() => {
    if (flagSignal > flagHandledRef.current) {
      flagHandledRef.current = flagSignal;
      setFlagMode(true);
    }
  }, [flagSignal]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [notes]);

  const handleSend = async () => {
    const text = draft.trim();
    if (!text) return;

    try {
      const res = await axios.post(
        `/api/livekit/meetings/${meetingId}/notes`,
        { type: flagMode ? 'flag' : 'note', text, elapsed_seconds: flagMode ? flaggedAtSeconds : 0 },
        { withCredentials: true }
      );
      if (res.data.success) {
        setNotes((prev) => [...prev, res.data.note]);
        setDraft('');
        setFlagMode(false);
      }
    } catch (err) {
      console.error('Failed to add note:', err);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const format = (marker) => {
    const result = applyFormat(textareaRef, marker);
    if (!result) return;
    setDraft(result.value);
    requestAnimationFrame(() => {
      textareaRef.current?.setSelectionRange(result.selectionStart, result.selectionEnd);
      textareaRef.current?.focus();
    });
  };

  return (
    <div className="velvet-side-panel velvet-notes-panel">
      <div className="velvet-panel-header">
        <span>
          <NotesIcon /> Notes
        </span>
        <button className="velvet-panel-collapse-btn" onClick={onToggle} title="Collapse">
          <CollapseIcon />
        </button>
      </div>

      <div className="velvet-panel-list" ref={listRef}>
        {notes.map((note) => (
          <div key={note.id} className={`velvet-note-entry ${note.type === 'flag' ? 'flagged' : ''}`}>
            <div className="velvet-note-entry-meta">
              {note.type === 'flag' ? (
                <>
                  <BookmarkIcon />
                  <span>Flagged Moment, {formatElapsed(note.elapsed_seconds)}</span>
                </>
              ) : (
                <span>{formatWallClock(note.created_at)}</span>
              )}
            </div>
            <div className="velvet-note-entry-text" dangerouslySetInnerHTML={{ __html: renderFormattedText(note.text) }} />
          </div>
        ))}
      </div>

      <div className={`velvet-panel-compose ${flagMode ? 'flag-mode' : ''}`}>
        {flagMode && (
          <div className="velvet-compose-flag-label">
            <BookmarkIcon /> Flagging moment at {formatElapsed(flaggedAtSeconds)}
            <button type="button" className="velvet-compose-flag-cancel" onClick={() => setFlagMode(false)}>
              &times;
            </button>
          </div>
        )}
        <textarea
          ref={textareaRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={flagMode ? 'What happened?' : 'Add a note...'}
        />
        <div className="velvet-panel-compose-toolbar">
          <button type="button" onClick={() => format('bold')}>
            <strong>B</strong>
          </button>
          <button type="button" onClick={() => format('italic')}>
            <em>I</em>
          </button>
          <button type="button" onClick={() => format('underline')}>
            <u>U</u>
          </button>
          <button type="button" className="velvet-panel-send" onClick={handleSend} title="Add">
            <SendIcon />
          </button>
        </div>
      </div>
    </div>
  );
};
